import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartFrame } from '../../../components/chart/chart-frame';
import { FLOW_VAR, RAG_VAR } from '../../../components/chart/rag';
import { errorMessage } from '../../../lib/error-message';
import { type ReportRange, reportApi, reportKeys } from '../api';

/**
 * Xu hướng Change Request — spec 16.4, "tín hiệu sớm của scope creep".
 *
 * Cột **đã đề xuất** là cột phải nhìn, không phải cột đã duyệt: nhịp *đề xuất*
 * mới nói lên phạm vi đang bị đẩy, còn tỉ lệ duyệt nói về cách PM phản ứng. Vì
 * vậy một CR bị từ chối vẫn được đếm là đã đề xuất.
 */
export function CrTrendChart({
  projectId,
  range,
}: {
  projectId: string;
  range: ReportRange;
}): ReactNode {
  const { t } = useTranslation();

  const report = useQuery({
    queryKey: reportKeys.crTrend(projectId, range),
    queryFn: () => reportApi.crTrend(projectId, range),
  });

  const buckets = report.data?.buckets ?? [];

  return (
    <ChartFrame
      title={t('reports.crTrendTitle')}
      help="crTrend"
      subtitle={t('reports.crTrendSubtitle', { count: report.data?.totalCreated ?? 0 })}
      asOf={range.asOf ? new Date(range.asOf) : undefined}
      loading={report.isPending}
      error={report.isError ? errorMessage(report.error, t) : null}
      onRetry={() => void report.refetch()}
      empty={
        report.data && report.data.totalCreated === 0
          ? { title: t('reports.crTrendEmpty'), description: t('reports.crTrendEmptyHelp') }
          : null
      }
      legend={[
        { label: t('reports.crCreated'), color: FLOW_VAR.inProgress },
        { label: t('reports.crApproved'), color: FLOW_VAR.done },
        { label: t('reports.crRejected'), color: RAG_VAR.red },
        { label: t('reports.crApplied'), color: RAG_VAR.unknown },
      ]}
    >
      <div className="h-44 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={buckets}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="var(--color-border)" />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="var(--color-border)" />
            <Tooltip
              contentStyle={{
                background: 'var(--color-background)',
                border: '1px solid var(--color-border)',
                fontSize: 12,
              }}
            />
            <Bar dataKey="created" name={t('reports.crCreated')} fill={FLOW_VAR.inProgress} />
            <Bar dataKey="approved" name={t('reports.crApproved')} fill={FLOW_VAR.done} />
            <Bar dataKey="rejected" name={t('reports.crRejected')} fill={RAG_VAR.red} />
            <Bar dataKey="applied" name={t('reports.crApplied')} fill={RAG_VAR.unknown} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartFrame>
  );
}
